const formatCurrency = (n) => {
  if (n == null || n === '' || isNaN(n)) return '';
  return new Intl.NumberFormat('en-CA', {
    minimumFractionDigits: 0,
    maximumFractionDigits: 2
  }).format(Number(n));
};

const parseCurrency = (str) => {
  if (str == null) return NaN;
  const s = String(str).trim();

  const euroLike = /^\d{1,3}(\.\d{3})+(,\d{1,2})?$/;
  if (euroLike.test(s)) {
    return parseFloat(s.replace(/\./g, '').replace(',', '.'));
  }

  return parseFloat(s.replace(/,/g, ''));
};

if (!customElements.get('algolia-product-grid')) {
  customElements.define(
    'algolia-product-grid',
    class AlgoliaProductGrid extends HTMLElement {
      constructor() {
        super();
        this.isInit = false;
        this.isSearchPage = this.hasAttribute('data-search-page');
        this.algoliaClient = false;
        this.currentIndex = 'shopify_products';
        this.productsPerPage = 50;
        this.page = 0;
        this.totalPages = 0;
        this.totalHits = 0;
        this.appliedFilters = '';
        this.query = '';
        this.lastQueryID = null;
        this.originalPriceRange = null;
        this.disjunctiveFacets = [];
        this.nonDisjunctiveFacets = [
          'inventory_available',
          'price',
          'collection_ids',
          'price_range',
          'tags'
        ];
        this.facetsExcludedFromDisplay = [
          'collection_ids',
          'price_range',
          'tags'
        ];

        if (this.inputMin && this.inputMax) {
          this.inputMin.addEventListener('focus', () => this.inputMin.select());
          this.inputMax.addEventListener('focus', () => this.inputMax.select());
          this.inputMin.addEventListener('change', this.onInputMinChange.bind(this));
          this.inputMax.addEventListener('change', this.onInputMaxChange.bind(this));
          this.inputMin.addEventListener('blur', () => { this.inputMin.value = formatCurrency(parseCurrency(this.inputMin.value)); });
          this.inputMax.addEventListener('blur', () => { this.inputMax.value = formatCurrency(parseCurrency(this.inputMax.value)); });
        }
      }

      getBaseFilters() {
        if (!this.isSearchPage) return [];

        return [
          'NOT tags:"Bottle Deposit"'
        ];
      }

      connectedCallback() {    
        if (this.isInit) return;     
        
        if (window.algoliaIsLoaded) {
          this.init();
          return;
        }       
        
        window.addEventListener('algolia:script-loaded', () => {
          this.init();
        });
      }

      async init() {
        if (this.isInit) return;     
        this.isInit = true;

        const searchParams = new URLSearchParams(window.location.search);
        const initialFilterString = searchParams.get('filters') ? decodeURI(searchParams.get('filters')) : false;
        const initialIndex = searchParams.get('index') ? decodeURI(searchParams.get('index')) : false;
        this.page = searchParams.get('page') ? parseInt(searchParams.get('page')) - 1 : this.page;
        this.query = searchParams.get('q') ? decodeURI(searchParams.get('q')) : '';

        if (initialIndex) {
          this.currentIndex = initialIndex;
          // Update both sort displays
          this.updateSortDisplays(initialIndex);
        }

        this.customer = this.dataset.customerId || false;
        this.collectionHandle = this.dataset.collectionHandle;
        this.collectionId = this.dataset.collectionId || false;
        this.appliedFilters = initialFilterString || this.buildFilterString();
        this.algoliaClient = window.algoliasearch('25TO6MPUL0', '1aa0c19fe6a0931340570bd358c2c9d2');
        this.algoliaClient.initIndex(this.currentIndex);
        // STREAM COMMENTED OUT: await this.initPriceRangeLimits();
        // this.fetchFilteredResults();
        this.fetchFilteredResults(true, true, true, { hydratePriceRange: true });
        this.addDelegatedEventListeners();
      }

      escapeHtml(text) {
        const div = document.createElement('div');
        div.textContent = String(text ?? '');
        return div.innerHTML;
      }

      addDelegatedEventListeners() {
        this.addEventListener('click', (e) => {
          this.handleProductCardClick(e);
        });
        
        document.addEventListener('change', theme.utils.debounce((e) => {
          if (e.target.name === 'sort_by' && e.target.closest('.select-sort-by')) {
            const sortValue = e.target.value;
            if (!sortValue) return;
            
            this.currentIndex = sortValue;
            this.page = 0;
            
            this.updateSortDisplays(sortValue);
            
            const searchParams = new URLSearchParams(window.location.search);
            searchParams.set('index', sortValue);
            searchParams.delete('page');
            history.replaceState(`Filtered Collection - ${this.collectionHandle}`, '', `?${searchParams.toString()}`);
            
            this.fetchFilteredResults();
            this.renderActiveFacets();
            
            return;
          }
          
          const actionEl = e.target.closest('[data-action]');
          if (!actionEl) return;

          const {
            action,
            removeValue
          } = actionEl.dataset;

          const searchParams = new URLSearchParams(window.location.search);

          switch(action) {
            case 'algolia-filter-change':
              this.appliedFilters = this.buildFilterString();
              this.page = 0;
              this.fetchFilteredResults();
              searchParams.set('filters', encodeURI(this.appliedFilters));
              searchParams.delete('page');
              
              history.replaceState(`Filtered Collection - ${this.collectionHandle}`, '', `?${searchParams.toString()}`);
              
              this.renderActiveFacets();
            break;

            default: break;
          }
        }), 200);

        document.addEventListener('click', theme.utils.debounce((e) => {
          const actionEl = e.target.closest('[data-action]');
          if (!actionEl) return;

          // Skip if it's a checkbox (handled by change event)
          if (actionEl.type === 'checkbox') return;

          const {
            action,
            removeValue
          } = actionEl.dataset;

          const searchParams = new URLSearchParams(window.location.search);

          switch(action) {
            case 'algolia-filter-change':
              this.appliedFilters = this.buildFilterString();
              this.page = 0;
              this.fetchFilteredResults();
              searchParams.set('filters', encodeURI(this.appliedFilters));
              searchParams.delete('page');
              
              history.replaceState(`Filtered Collection - ${this.collectionHandle}`, '', `?${searchParams.toString()}`);
            break;

            case 'algolia-price-range':
              clearTimeout(this.priceRangeTimeout);
              this.priceRangeTimeout = setTimeout(() => {
                this.appliedFilters = this.buildFilterString();
                this.page = 0;
                this.fetchFilteredResults();
                searchParams.set('filters', encodeURI(this.appliedFilters));
                searchParams.delete('page');
                
                history.replaceState(`Filtered Collection - ${this.collectionHandle}`, '', `?${searchParams.toString()}`);

                this.renderActiveFacets();
              }, 300);
            break;

            case 'algolia-remove-facet':
              {
                const facetType = actionEl.dataset.facetType;
                const facetGroup = actionEl.dataset.facetGroup;
                const facetValue = actionEl.dataset.facetValue;
                
                this.removeFacet(facetType, facetGroup, facetValue);
              }
            break;

            case 'algolia-clear-all-facets':
              this.clearAllFacets();
            break;

            case 'algolia-pagination-previous':
              if (this.page > 0) {
                this.page--;
                this.updateURLWithPage();
                this.scrollToTop();
                setTimeout(() => {
                  this.fetchFilteredResults(true, true, true);
                }, 100);
              }
            break;

            case 'algolia-pagination-next':
              if (this.page < this.totalPages - 1) {
                this.page++;
                this.updateURLWithPage();
                this.scrollToTop();
                setTimeout(() => {
                  this.fetchFilteredResults(true, true, true);
                }, 100);
              }
            break;

            case 'algolia-pagination-goto':
              {
                const targetPage = parseInt(actionEl.dataset.page);
                if (targetPage >= 0 && targetPage < this.totalPages && targetPage !== this.page) {
                  this.page = targetPage;
                  this.updateURLWithPage();
                  this.scrollToTop();
                  setTimeout(() => {
                    this.fetchFilteredResults(true, true, true);
                  }, 100);
                }
              }
            break;

            default: break;
          }
        }), 200);
      }

      buildFilterString() {
        let activeFilterCount = 0;
        
        // Handle regular filter groups
        const filterGroups = document.querySelectorAll('[data-algolia-filter-group]');
        
        const appliedFilters = [...filterGroups].map((filterGroup) => {
          const groupName = filterGroup.dataset.algoliaFilterGroup;
          const checkedCheckboxes = filterGroup.querySelectorAll('input[type="checkbox"][data-action="algolia-filter-change"]:checked');
          const appliedValues = [...checkedCheckboxes]
            .map(({ value }) => `${groupName}:"${value}"`).join(' OR ');
          
          // Count active filters in this group
          activeFilterCount += checkedCheckboxes.length;
          
          if (!appliedValues.length) return;
          
          return `(${appliedValues})`;
        }).filter((x) => !!x);
        
        // Handle inventory filter separately
        const inventoryFilter = document.querySelector('[data-inventory-filter]');
        if (inventoryFilter) {
          const inventoryCheckbox = inventoryFilter.querySelector('input[type="checkbox"][data-action="algolia-filter-change"]');
          if (inventoryCheckbox && inventoryCheckbox.checked) {
            activeFilterCount += 1;
            if (!appliedFilters.includes('(inventory_available:"true")')) {
              appliedFilters.push('(inventory_available:"true")');
            }
          } else {
            // Remove inventory filter if it exists in the array (shouldn't happen, but for safety)
            const inventoryFilterIndex = appliedFilters.findIndex(filter => filter.includes('inventory_available'));
            if (inventoryFilterIndex !== -1) {
              appliedFilters.splice(inventoryFilterIndex, 1);
            }
          }
        }
        
        // Handle price range filter separately
        const priceRangeFilter = document.querySelector('[data-price-range-filter]');
        if (priceRangeFilter) {
          const minInput = priceRangeFilter.querySelector('#AlgoliaPriceInputFrom');
          const maxInput = priceRangeFilter.querySelector('#AlgoliaPriceInputTo');
          
          if (minInput && maxInput) {
            const minPrice = parseCurrency(minInput.value);
            const maxPrice = parseCurrency(maxInput.value);
            const minRange = parseFloat(minInput.getAttribute('min'));
            const maxRange = parseFloat(maxInput.getAttribute('max'));
            
            // Only add price filter if it's not at the full range
            if (minPrice > minRange || maxPrice < maxRange) {
              activeFilterCount += 1;
              appliedFilters.push(`price:${minPrice} TO ${maxPrice}`);
            }
          }
        }
        
        // Update facet count elements
        this.updateFacetCount(activeFilterCount);

        const finalAppliedFilters = appliedFilters.join(' AND ');
        const filterParts = [];
        const baseFilters = this.getBaseFilters();

        if (baseFilters.length) filterParts.push(...baseFilters);
        if (this.collectionId) filterParts.push(`collection_ids:${this.collectionId}`);
        if (finalAppliedFilters.length) filterParts.push(finalAppliedFilters);

        return filterParts.join(' AND ');
      }

      // STREAM COMMENTED OUT: async fetchFilteredResults(filters = true, products = true, replace = true) {
      async fetchFilteredResults(filters = true, products = true, replace = true, opts = {}) {
        const { hydratePriceRange = false } = opts;
        /*STREAM COMMENTED OUT: const mainSearchParams = {
          facets: '*',
          filters: this.appliedFilters,
          hitsPerPage: this.productsPerPage,
          page: this.page,
          query: this.query,
          clickAnalytics: true,
          ruleContexts: this.collectionHandle
        };*/
        const mainSearchParams = {
          facets: ['price', '*'], // or better: a specific list instead of '*'
          filters: this.appliedFilters,
          hitsPerPage: this.productsPerPage,
          page: this.page,
          query: this.query,
          clickAnalytics: true,
          maxValuesPerFacet: 100 // optional, only if you need more facet values
        };
        if (this.isSearchPage) {
          mainSearchParams.optionalFilters = ['inventory_available:true'];
        }

        if (this.collectionHandle) {
          mainSearchParams.ruleContexts = this.collectionHandle;
        }

        const queries = [{
          indexName: this.currentIndex,
          params: mainSearchParams,
        }];

        if (filters && this.disjunctiveFacets.length > 0) {
          const facetFiltersExcludingDisjunctive = this.buildFilterStringExcludingDisjunctiveFacets();
          
          queries.push({
            indexName: this.currentIndex,
            params: {
              facets: '*',
              filters: facetFiltersExcludingDisjunctive,
              hitsPerPage: 0,
              page: 0,
              query: this.query,
              clickAnalytics: false,
              attributesToRetrieve: [],
              attributesToHighlight: [],
              attributesToSnippet: []
            }
          });
        }

        await this.algoliaClient.multipleQueries(queries, 1, false).then((response) => {
          const mainResult = response.results[0];
          if ((hydratePriceRange || !this.originalPriceRange) && mainResult?.facets_stats?.price) {
            const stats = mainResult.facets_stats.price;
            if (Number.isFinite(stats.min) && Number.isFinite(stats.max)) {
              const to2 = (n) => Number(n.toFixed(2));
              this.originalPriceRange = { min: to2(stats.min), max: to2(stats.max) };
            }
          }
          const facetResult = queries.length > 1 ? response.results[1] : null;
          
          if (this.disjunctiveFacets.length === 0 && mainResult?.facets) {
            this.disjunctiveFacets = this.getDisjunctiveFacets(mainResult.facets);
          }
          
          // Merge facet results: use facet query for disjunctive facets, main query for others
          let mergedFacets = mainResult?.facets || {};
          if (facetResult?.facets && mainResult?.facets) {
            mergedFacets = { ...mainResult.facets };
            // Replace disjunctive facet values with unfiltered ones from facet query
            this.disjunctiveFacets.forEach(facetKey => {
              if (facetResult.facets[facetKey]) {
                mergedFacets[facetKey] = facetResult.facets[facetKey];
              }
            });
          }
          
          // Create merged result object
          const result = {
            ...mainResult,
            facets: mergedFacets
          };
          
          if (mainResult) {
            // Store the queryID for click tracking
            this.lastQueryID = mainResult.queryID;
            
            // Update pagination data
            this.totalHits = mainResult.nbHits;
            this.totalPages = mainResult.nbPages;
            
            if (filters) {
              this.renderFilters(result);
            }
            if (products) {
              this.renderProducts(mainResult?.hits || [], replace);
            }
            
            // Update pagination UI
            this.renderPagination();
          } else {
            if (filters) {
              this.renderFilters(result);
            }
            if (products) {
              this.renderProducts([], replace);
            }
            
            // Update pagination UI for no results
            this.renderPagination();
          }
        });
      }

      async initPriceRangeLimits() {
        if (this.originalPriceRange) return;

        const baseFilters = [];
        if (this.collectionId) baseFilters.push(`collection_ids:${this.collectionId}`);

        const res = await this.algoliaClient.multipleQueries([{
          indexName: this.currentIndex,
          params: {
            facets: ['price'],
            filters: baseFilters.join(' AND '),
            hitsPerPage: 0,
            page: 0,
            query: '',
            clickAnalytics: false,
            analytics: false,
            attributesToRetrieve: [],
            attributesToHighlight: [],
            attributesToSnippet: [],
            maxValuesPerFacet: 1
          }
        }], { strategy: 'none' });

        const stats = res?.results?.[0]?.facets_stats?.price;
        if (stats && Number.isFinite(stats.min) && Number.isFinite(stats.max)) {
          const to2 = (n) => Number(n.toFixed(2));
          this.originalPriceRange = {
            min: to2(stats.min),
            max: to2(stats.max)
          };
        }
      }

      renderCheckbox = ({ label, value, id, formatAsPrice, filterGroup }) => {
        const searchParams = new URLSearchParams(window.location.search);
        const currentFilters = searchParams.get('filters') ? decodeURI(searchParams.get('filters')) : '';
        
        // Check if this specific filter group + value combination is active
        const specificFilterPattern = `${filterGroup}:"${value}"`;
        const isChecked = currentFilters.includes(specificFilterPattern);

        return `
          <input 
            title="${label}"
            ${isChecked ? 'checked' : ''}
            id="${id}"
            type="checkbox"
            value="${value}"
            data-action="algolia-filter-change"
          >
          <label for="${id}">
            ${formatAsPrice ? `$${this.escapeHtml(label).replaceAll(':', ' - $')}` : this.escapeHtml(label)}
          </label>
        `;
      }

      renderFilterList(filterName, subValues, id) {
        let label;
        label = filterName.replaceAll('_', ' ').replaceAll('-', ' ').replaceAll('meta.custom.', '');

        const orderedSubvalues = subValues.sort((a,b)=>a.localeCompare(b,'en',{sensitivity:'base'}));
        
        if (id === 'product_type') {
          label = 'Product Type';
        } else if (label === 'abv') {
          label = 'Alc/Vol';
        } else if (label === 'size') {
          label = 'Size/Volume';
        } else if (label === 'producer') {
          label = 'Producer/Distillery';
        } else if (label === 'alcohol type') {
          label = 'Category';
        } else {
          label = label.split(' ').map(word => word.charAt(0).toUpperCase() + word.slice(1).toLowerCase()).join(' ');
        }

        // Check if this filter group has any active filters
        const searchParams = new URLSearchParams(window.location.search);
        const currentFilters = searchParams.get('filters') ? decodeURI(searchParams.get('filters')) : '';
        const hasActiveFilters = currentFilters.includes(`${filterName}:`);
        
        // Set open attribute and aria-expanded based on active filters
        const openAttribute = hasActiveFilters ? 'open' : '';
        const ariaExpanded = hasActiveFilters ? 'aria-expanded="true"' : 'aria-expanded="false"';

        return `<details class="details active" is="accordion-details" data-filter ${openAttribute} ${ariaExpanded}>
          <summary class="details__summary flex items-center justify-between gap-2 cursor-pointer">
            <span class="text-base-xl font-bold">${label}</span>
            <svg class="icon icon-chevron-up icon-md" viewBox="0 0 24 24" stroke="currentColor" fill="none" xmlns="http://www.w3.org/2000/svg">
              <path stroke-linecap="round" stroke-linejoin="round" d="M6 15L12 9L18 15"></path>
            </svg>
          </summary>
          <div class="details__content">
            <ul class="list grid gap-3" role="list" data-algolia-filter-group="${filterName}" data-filter-name="${filterName}">
              ${orderedSubvalues.sort((a, b) => a-b).map((value, i) => `
                <li class="flex items-center gap-3">
                  ${this.renderCheckbox({
                    value, 
                    label: value.replaceAll('_', ' ').replaceAll('-', ' '), 
                    id: `filter-value-${filterName}-${value.replaceAll(' ', '-')}-${i}`,
                    formatAsPrice: filterName.toLowerCase()?.includes('price'),
                    filterGroup: filterName
                  })}
                </li>
              `).join('')}
            </ul>
          </div>
        </details>`;
      }

      renderInStockFilter() {
        // Check if the checkbox already exists in the DOM and get its current state
        const existingCheckbox = document.getElementById('in-stock-filter');
        let isChecked = false;
        
        if (existingCheckbox) {
          // If checkbox exists, use its current state
          isChecked = existingCheckbox.checked;
        } else {
          // If checkbox doesn't exist, check URL parameters for initial state
          const searchParams = new URLSearchParams(window.location.search);
          const currentFilters = searchParams.get('filters') ? decodeURI(searchParams.get('filters')) : '';
          isChecked = currentFilters.includes('inventory_available:"true"');
        }

        return `
          <div class="details" data-inventory-filter>
            <div class="details__summary flex items-center justify-between gap-2">
              <label for="in-stock-filter" class="text-base-xl font-bold cursor-pointer">In Stock Only</label>
              <input id="in-stock-filter" class="switch" name="in-stock-filter" type="checkbox" value="true" ${isChecked ? 'checked' : ''} data-action="algolia-filter-change">
            </div>
          </div>
        `;
      }

      renderPriceRangeFilter() {
        if (!this.originalPriceRange) return '';

        const searchParams = new URLSearchParams(window.location.search);
        const currentFilters = searchParams.get('filters') ? decodeURI(searchParams.get('filters')) : '';
        const match = currentFilters.match(/price:(\d+(?:\.\d+)?)\s+TO\s+(\d+(?:\.\d+)?)/);

        const minExt = this.originalPriceRange.min;
        const maxExt = this.originalPriceRange.max;

        const minSel = match ? parseFloat(match[1]) : minExt;
        const maxSel = match ? parseFloat(match[2]) : maxExt;

        const lowerBoundProgress = (minSel / maxExt) * 100;
        const higherBoundProgress = (maxSel / maxExt) * 100;

        return `
          <details class="details active" is="accordion-details" open data-price-range-filter>
            <summary class="details__summary flex items-center justify-between gap-2 cursor-pointer">
              <span class="text-base-xl font-bold">Price</span>
              <svg class="icon icon-chevron-up icon-md" viewBox="0 0 24 24" stroke="currentColor" fill="none" xmlns="http://www.w3.org/2000/svg">
                <path stroke-linecap="round" stroke-linejoin="round" d="M6 15L12 9L18 15"></path>
              </svg>
            </summary>
            <div class="details__content">
              <price-range class="price-range grid gap-8" data-min-price="${minExt}" data-max-price="${maxExt}">
                <div class="range-wrapper relative" style="--range-min: ${lowerBoundProgress}%; --range-max: ${higherBoundProgress}%">
                  <input class="range w-full pointer-events-none" type="range"
                    min="${minExt}" max="${maxExt}" step="0.01"
                    value="${minSel}" id="AlgoliaPriceRangeFrom"
                    data-action="algolia-price-range" />
                  <input class="range w-full pointer-events-none absolute top-0 left-0" type="range"
                    min="${minExt}" max="${maxExt}" step="0.01"
                    value="${maxSel}" id="AlgoliaPriceRangeTo"
                    data-action="algolia-price-range" />
                </div>

                <div class="input-wrapper flex items-center gap-6">
                  <div class="field flex items-center grow relative">
                    <span class="prefix absolute left-0">$</span>
                    <input class="input" type="text" inputmode="decimal"
                      id="AlgoliaPriceInputFrom" autocomplete="off"
                      placeholder="${formatCurrency(minExt)}"
                      min="${minExt}" max="${maxExt}" step="0.01"
                      value="${formatCurrency(minSel)}"
                      data-action="algolia-price-range" />
                  </div>
                  <span>to</span>
                  <div class="field flex items-center grow relative">
                    <span class="prefix absolute left-0">$</span>
                    <input class="input" type="text" inputmode="decimal"
                      id="AlgoliaPriceInputTo" autocomplete="off"
                      placeholder="${formatCurrency(maxExt)}"
                      min="${minExt}" max="${maxExt}" step="0.01"
                      value="${formatCurrency(maxSel)}"
                      data-action="algolia-price-range" />
                  </div>
                </div>
              </price-range>
            </div>
          </details>
        `;
      }

      getOrderedFacetKeys(facets, facetOrder) {
        const allFacetKeys = Object.keys(facets);

        if (Array.isArray(facetOrder) && facetOrder.length) {
          return facetOrder.filter((name) => allFacetKeys.includes(name));
        }

        return allFacetKeys;
      }

      getDisjunctiveFacets(facets) {
        if (!facets || typeof facets !== 'object') return [];
        
        const allFacetKeys = Object.keys(facets);
        return allFacetKeys.filter(facetKey => !this.nonDisjunctiveFacets.includes(facetKey));
      }

      buildFilterStringExcludingDisjunctiveFacets() {
        
        // Handle regular filter groups - exclude disjunctive facets
        const filterGroups = document.querySelectorAll('[data-algolia-filter-group]');
        
        const appliedFilters = [...filterGroups]
          .filter(filterGroup => {
            const groupName = filterGroup.dataset.algoliaFilterGroup;
            // Exclude disjunctive facets from this filter string
            return this.nonDisjunctiveFacets.includes(groupName);
          })
          .map((filterGroup) => {
            const groupName = filterGroup.dataset.algoliaFilterGroup;
            const checkedCheckboxes = filterGroup.querySelectorAll('input[type="checkbox"][data-action="algolia-filter-change"]:checked');
            const appliedValues = [...checkedCheckboxes]
              .map(({ value }) => `${groupName}:"${value}"`).join(' OR ');
            
            if (!appliedValues.length) return;
            
            return `(${appliedValues})`;
          })
          .filter((x) => !!x);
        
        // Handle inventory filter (non-disjunctive)
        const inventoryFilter = document.querySelector('[data-inventory-filter]');
        if (inventoryFilter) {
          const inventoryCheckbox = inventoryFilter.querySelector('input[type="checkbox"][data-action="algolia-filter-change"]');
          if (inventoryCheckbox && inventoryCheckbox.checked) {
            if (!appliedFilters.includes('(inventory_available:"true")')) {
              appliedFilters.push('(inventory_available:"true")');
            }
          }
        }
        
        // Handle price range filter (non-disjunctive)
        const priceRangeFilter = document.querySelector('[data-price-range-filter]');
        if (priceRangeFilter) {
          const minInput = priceRangeFilter.querySelector('#AlgoliaPriceInputFrom');
          const maxInput = priceRangeFilter.querySelector('#AlgoliaPriceInputTo');
          
          if (minInput && maxInput) {
            const minPrice = parseCurrency(minInput.value);
            const maxPrice = parseCurrency(maxInput.value);
            const minRange = parseFloat(minInput.getAttribute('min'));
            const maxRange = parseFloat(maxInput.getAttribute('max'));
            
            // Only add price filter if it's not at the full range
            if (minPrice > minRange || maxPrice < maxRange) {
              appliedFilters.push(`price:${minPrice} TO ${maxPrice}`);
            }
          }
        }
        
        const finalAppliedFilters = appliedFilters.join(' AND ');
        const filterParts = [];
        const baseFilters = this.getBaseFilters();

        if (baseFilters.length) filterParts.push(...baseFilters);
        if (this.collectionId) filterParts.push(`collection_ids:${this.collectionId}`);
        if (finalAppliedFilters.length) filterParts.push(finalAppliedFilters);

        return filterParts.join(' AND ');
      }

      renderFilters(result) {
        if (!result || !result.facets) return;
        
        const filters = result.facets;
        const facetOrder = result.renderingContent?.facetOrdering?.facets?.order || [];
        
        const filtersWraps = document.querySelectorAll('[data-algolia-filters-wrap]'); 

        filtersWraps.forEach((filterWrap, i) => {
          const orderedFacetKeys = this.getOrderedFacetKeys(filters, facetOrder);
          
          filterWrap.innerHTML = `
            ${this.renderInStockFilter()}
            ${orderedFacetKeys.map((filter) => {
              if (this.facetsExcludedFromDisplay.includes(filter)) return;

              if (filter === 'price') {
                return this.renderPriceRangeFilter(filters[filter]);
              } else if (filter === 'inventory_available') {
              } else {
                const filterValues = Object.keys(filters[filter]);

                return this.renderFilterList(filter, filterValues, filter);
              }              
            }).join('')}
            ${!filters.price && this.originalPriceRange ? this.renderPriceRangeFilter({}) : ''}
          `;
        });
        
        // Update facet count after filters are rendered with a small delay to ensure price range is initialized
        setTimeout(() => {
          this.updateFacetCountFromCurrentState();
          this.renderActiveFacets();
        }, 100);
      }

      renderProducts(products, replace = true) {
        const productsWrap = this.querySelector('[data-products-wrap]');
        if (!productsWrap) return;

        const productCountElement = this.querySelector('[data-search-product-count]');
        if (productCountElement && this.totalHits) {
          productCountElement.textContent = `${this.totalHits} results`;
        }

        if (!products.length) {
          productsWrap.classList.add('card-grid--empty');
          productsWrap.innerHTML =
            `<intersection-loader 
              data-url="${this.collectionHandle ? `/collections/${this.collectionHandle}`: ''}" 
              data-section-id="algolia-no-results"
            >
            </intersection-loader>`
          ;
          return;
        };

        if (replace) productsWrap.innerHTML = '';

        products.forEach((product, index) => {
          productsWrap.classList.remove('card-grid--empty');
          productsWrap.insertAdjacentHTML(
            'beforeend',
            `<intersection-loader 
              data-url="${this.collectionHandle ? `/collections/${this.collectionHandle}`: ''}/products/${product.handle}" 
              data-section-id="product-card"
              data-object-id="${product.objectID}"
              data-position="${product.position}"
            >
            </intersection-loader>`
          );
        });
      }

      updateFacetCount(activeFilterCount) {
        const facetCountElements = document.querySelectorAll('[data-algolia-facet-count]');
        
        facetCountElements.forEach(element => {
          if (activeFilterCount > 0) {
            // Show the count and update the text
            element.removeAttribute('hidden');
            element.textContent = activeFilterCount;
          } else {
            // Hide the count and reset to 0
            element.setAttribute('hidden', '');
            element.textContent = '0';
          }
        });
      }

      updateFacetCountFromCurrentState() {
        let activeFilterCount = 0;
        
        // Count checked checkboxes in regular filter groups
        const checkedCheckboxes = document.querySelectorAll('[data-algolia-filter-group] input[type="checkbox"][data-action="algolia-filter-change"]:checked');
        activeFilterCount += checkedCheckboxes.length;
        
        // Count inventory filter
        const inventoryCheckbox = document.querySelector('[data-inventory-filter] input[type="checkbox"][data-action="algolia-filter-change"]:checked');
        if (inventoryCheckbox) {
          activeFilterCount += 1;
        }
        
        // Count price range filter
        const priceRangeFilter = document.querySelector('[data-price-range-filter]');
        if (priceRangeFilter) {
          const minInput = priceRangeFilter.querySelector('#AlgoliaPriceInputFrom');
          const maxInput = priceRangeFilter.querySelector('#AlgoliaPriceInputTo');

          if (minInput && maxInput) {
            const minPrice = parseCurrency(minInput.value);
            const maxPrice = parseCurrency(maxInput.value);
            const minRange = parseFloat(minInput.getAttribute('min'));
            const maxRange = parseFloat(maxInput.getAttribute('max'));

            const tolerance = 0.01;
            const isAtFullRange =
              Math.abs(minPrice - minRange) <= tolerance &&
              Math.abs(maxPrice - maxRange) <= tolerance;

            if (!isAtFullRange) {
              activeFilterCount += 1;
            }
          }
        }
        
        // Update facet count elements
        this.updateFacetCount(activeFilterCount);
      }

      renderActiveFacets() {
        const activeFacetsContainer = document.querySelector('[data-algolia-active-facets]');
        if (!activeFacetsContainer) return;

        const activeFacets = [];
        
        // Get active checkboxes from regular filter groups
        const checkedCheckboxes = document.querySelectorAll('[data-algolia-filter-group] input[type="checkbox"][data-action="algolia-filter-change"]:checked');
        checkedCheckboxes.forEach(checkbox => {
          const filterGroup = checkbox.closest('[data-algolia-filter-group]');
          const groupName = filterGroup.dataset.algoliaFilterGroup;
          const label = checkbox.nextElementSibling?.textContent || checkbox.value;
          
          activeFacets.push({
            type: 'checkbox',
            groupName: groupName,
            value: checkbox.value,
            label: label,
            element: checkbox
          });
        });
        
        // Get active inventory filter
        const inventoryCheckbox = document.querySelector('[data-inventory-filter] input[type="checkbox"][data-action="algolia-filter-change"]:checked');
        if (inventoryCheckbox) {
          activeFacets.push({
            type: 'inventory',
            groupName: 'inventory_available',
            value: 'true',
            label: 'In Stock only',
            element: inventoryCheckbox
          });
        }
        
        // Get active price range filter
        const priceRangeFilter = document.querySelector('[data-price-range-filter]');
        if (priceRangeFilter) {
          const minInput = priceRangeFilter.querySelector('#AlgoliaPriceInputFrom');
          const maxInput = priceRangeFilter.querySelector('#AlgoliaPriceInputTo');
          
          if (minInput && maxInput) {
            const minPrice = parseCurrency(minInput.value);
            const maxPrice = parseCurrency(maxInput.value);
            
            const minRange = parseFloat(minInput.getAttribute('min'));
            const maxRange = parseFloat(maxInput.getAttribute('max'));
            
            const tolerance = 0.01;
            const isAtFullRange =
              Math.abs(minPrice - minRange) <= tolerance &&
              Math.abs(maxPrice - maxRange) <= tolerance;

            if (!isAtFullRange) {
              activeFacets.push({
                type: 'price_range',
                groupName: 'price',
                value: `${minPrice} TO ${maxPrice}`,
                label: `$${formatCurrency(minPrice)} - $${formatCurrency(maxPrice)}`,
                element: priceRangeFilter
              });
            }
          }
        }
        
        // Render active facets
        let activeFacetsHTML = '';
        
        activeFacets.forEach(facet => {
          activeFacetsHTML += `
            <div class="removable-facet flex items-center justify-between gap-3 lg:gap-4 text-xs lg:text-sm leading-none rounded-full" data-facet-type="${facet.type}" data-facet-group="${facet.groupName}" data-facet-value="${facet.value}">
              <div class="facet-title flex items-center gap-2">
                ${this.escapeHtml(facet.label)}
              </div>
              <button class="block relative" type="button" aria-label="Clear ${this.escapeHtml(facet.label)}" data-action="algolia-remove-facet" data-facet-type="${facet.type}" data-facet-group="${facet.groupName}" data-facet-value="${facet.value}">
                <span class="btn-text">
                  <svg class="icon icon-remove icon-xs" viewBox="0 0 24 24" stroke="currentColor" fill="none" xmlns="http://www.w3.org/2000/svg">
                    <path stroke-linecap="round" stroke-linejoin="round" d="M6 18L18 6M6 6l12 12"></path>
                  </svg>
                </span>
              </button>
            </div>
          `;
        });
        
        // Add Clear All button if more than 1 active filter
        if (activeFacets.length > 1) {
          activeFacetsHTML += `
            <button class="facet-clear-all link text-xs lg:text-sm" type="button" data-action="algolia-clear-all-facets">
              Clear all
            </button>
          `;
        }
        
        activeFacetsContainer.innerHTML = activeFacetsHTML;
      }

      updateSortDisplays(sortValue) {
        // Update the algolia-facet-sort display
        const sortElement = this.querySelector('algolia-facet-sort');
        if (sortElement) {
          const sortInput = sortElement.querySelector(`input[value="${sortValue}"]`);
          if (sortInput) {
            sortInput.checked = true;
            const sortLabel = sortInput.nextElementSibling;
            if (sortLabel) {
              const selectionLabel = sortElement.querySelector('[data-selection-label]');
              if (selectionLabel) {
                selectionLabel.textContent = sortLabel.textContent;
              }
            }
          }
        }
        
        // Update the sort select dropdown
        const sortSelect = this.querySelector('select[name="sort_by"]');
        if (sortSelect) {
          sortSelect.value = sortValue;
        }
      }

      updateURLWithPage() {
        const searchParams = new URLSearchParams(window.location.search);
        if (this.page > 0) {
          searchParams.set('page', (this.page + 1).toString());
        } else {
          searchParams.delete('page');
        }
        history.replaceState(`Filtered Collection - ${this.collectionHandle}`, '', `?${searchParams.toString()}`);
      }

      scrollToTop() {
        // Smooth scroll to the top of the collection section
        const collectionSection = this.closest('.collection');
        if (collectionSection) {
          collectionSection.scrollIntoView({ 
            behavior: 'smooth', 
            block: 'start' 
          });
        } else {
          // Fallback to window scroll if collection section not found
          window.scrollTo({ 
            top: 0, 
            behavior: 'smooth' 
          });
        }
      }

      renderPagination() {
        const paginationContainer = document.querySelector('[data-algolia-pagination]');
        if (!paginationContainer) return;

        // Hide pagination if no results or only one page
        if (this.totalHits === 0 || this.totalPages <= 1) {
          paginationContainer.style.display = 'none';
          return;
        }

        paginationContainer.style.display = 'flex';

        const prevButton = paginationContainer.querySelector('[data-algolia-pagination-previous]');
        const nextButton = paginationContainer.querySelector('[data-algolia-pagination-next]');
        const pagesContainer = paginationContainer.querySelector('[data-algolia-pagination-pages]');

        // Update previous button
        if (prevButton) {
          if (this.page > 0) {
            prevButton.classList.remove('disabled', 'opacity-50', 'pointer-events-none');
            prevButton.style.pointerEvents = 'auto';
          } else {
            prevButton.classList.add('disabled', 'opacity-50', 'pointer-events-none');
            prevButton.style.pointerEvents = 'none';
          }
        }

        // Update next button
        if (nextButton) {
          if (this.page < this.totalPages - 1) {
            nextButton.classList.remove('disabled', 'opacity-50', 'pointer-events-none');
            nextButton.style.pointerEvents = 'auto';
          } else {
            nextButton.classList.add('disabled', 'opacity-50', 'pointer-events-none');
            nextButton.style.pointerEvents = 'none';
          }
        }

        // Generate page numbers
        if (pagesContainer) {
          pagesContainer.innerHTML = this.generatePageNumbers();
        }
      }

      generatePageNumbers() {
        const currentPage = this.page + 1; // Convert to 1-based for display
        const totalPages = this.totalPages;
        const pages = [];

        // Always show first page
        pages.push(this.createPageLink(1, currentPage === 1));

        // Calculate range around current page
        const range = 2; // Show 2 pages before and after current page
        const start = Math.max(2, currentPage - range);
        const end = Math.min(totalPages - 1, currentPage + range);

        // Add ellipsis after first page if needed
        if (start > 2) {
          pages.push(this.createEllipsis());
        }

        // Add pages around current page
        for (let i = start; i <= end; i++) {
          pages.push(this.createPageLink(i, i === currentPage));
        }

        // Add ellipsis before last page if needed
        if (end < totalPages - 1) {
          pages.push(this.createEllipsis());
        }

        // Always show last page (if different from first page)
        if (totalPages > 1) {
          pages.push(this.createPageLink(totalPages, currentPage === totalPages));
        }

        return pages.join('');
      }

      createPageLink(pageNumber, isCurrent) {
        const pageIndex = pageNumber - 1; // Convert back to 0-based for internal use
        
        if (isCurrent) {
          return `<span class="page current flex items-center justify-center rounded-full pointer-events-none" aria-current="page" aria-label="Page ${pageNumber}"><span class="btn-text" data-text>${pageNumber}</span></span>`;
        } else {
          // return `<a href="#" class="page flex items-center justify-center rounded-full" data-action="algolia-pagination-goto" data-page="${pageIndex}" aria-label="Page ${pageNumber}" is="magnet-link"><span class="btn-text" data-text>${pageNumber}</span></a>`;
          return `<button type="button" class="page flex items-center justify-center rounded-full" data-action="algolia-pagination-goto" data-page="${pageIndex}" aria-label="Page ${pageNumber}"><span class="btn-text" data-text>${pageNumber}</span></button>`;
        }
      }

      createEllipsis() {
        return `<span class="page flex items-center justify-center rounded-full" style="pointer-events: none; opacity: 0.9;">...</span>`;
      }

      removeFacet(facetType, facetGroup, facetValue) {
        if (facetType === 'checkbox') {
          // Find and uncheck the checkbox
          const checkbox = document.querySelector(`[data-algolia-filter-group="${facetGroup}"] input[value="${facetValue}"]`);
          if (checkbox) {
            checkbox.checked = false;
            checkbox.dispatchEvent(new Event('change', { bubbles: true }));
          }
        } else if (facetType === 'inventory') {
          // Uncheck inventory filter
          const inventoryCheckbox = document.querySelector('[data-inventory-filter] input[type="checkbox"]');
          if (inventoryCheckbox) {
            inventoryCheckbox.checked = false;
            inventoryCheckbox.dispatchEvent(new Event('change', { bubbles: true }));
          }
        } else if (facetType === 'price_range') {
          // Reset price range to full range
          const priceRangeFilter = document.querySelector('[data-price-range-filter]');
          if (priceRangeFilter && this.originalPriceRange) {
            const minInput = priceRangeFilter.querySelector('#AlgoliaPriceInputFrom');
            const maxInput = priceRangeFilter.querySelector('#AlgoliaPriceInputTo');
            
            if (minInput && maxInput) {
              minInput.value = formatCurrency(this.originalPriceRange.min);
              maxInput.value = formatCurrency(this.originalPriceRange.max);

              // Update range sliders
              const rangeMin = priceRangeFilter.querySelector('input[type="range"]:first-child');
              const rangeMax = priceRangeFilter.querySelector('input[type="range"]:last-child');
              
              if (rangeMin && rangeMax) {
                rangeMin.value = this.originalPriceRange.min;
                rangeMax.value = this.originalPriceRange.max;
                
                // Update visual styles
                const priceRange = priceRangeFilter.querySelector('price-range');
                if (priceRange && priceRange.updateRangeStyles) {
                  priceRange.updateRangeStyles();
                }
              }
              
              // Trigger change event
              minInput.dispatchEvent(new Event('change', { bubbles: true }));
            }
          }
        }
        
        // Update filters and results
        this.appliedFilters = this.buildFilterString();
        this.page = 0;
        this.fetchFilteredResults();
        
        // Update URL
        const searchParams = new URLSearchParams(window.location.search);
        searchParams.set('filters', encodeURI(this.appliedFilters));
        searchParams.delete('page'); // Reset page when filters change
        
        history.replaceState(`Filtered Collection - ${this.collectionHandle}`, '', `?${searchParams.toString()}`);
      }

      handleProductCardClick(event) {
        const productCard = event.target.closest('[data-object-id]');
        if (!productCard) return;

        const objectId = productCard.getAttribute('data-object-id');
        const position = productCard.getAttribute('data-position');
        
        if (!objectId || !this.lastQueryID) return;  

        if (event.target.closest('button[type="submit"]')) {
          this.sendAlgoliaATCEvent(objectId);
          return
        }

        // Send clickedObjectIDsAfterSearch event to Algolia
        if (typeof aa !== 'function') {
          console.warn('Algolia analytics not loaded');
          return;
        }

        aa('clickedObjectIDsAfterSearch', {
          userToken: this.customer || 'guest',
          eventName: 'Product Clicked',
          index: this.currentIndex,
          queryID: this.lastQueryID,
          objectIDs: [objectId],
          positions: [position ? parseInt(position) : 1]
        });
      }

      sendAlgoliaATCEvent(objectId) {
        aa('addedToCartObjectIDsAfterSearch', {
          userToken: this.customer || 'guest',
          eventName: 'Added To Cart Object Ids After Search',
          index: this.currentIndex,
          queryID: this.lastQueryID,
          objectIDs: [objectId],
        });
      }

      clearAllFacets() {
        // Uncheck all checkboxes
        const allCheckboxes = document.querySelectorAll('[data-action="algolia-filter-change"]:checked');
        allCheckboxes.forEach(checkbox => {
          checkbox.checked = false;
        });
        
        // Reset price range to full range
        const priceRangeFilter = document.querySelector('[data-price-range-filter]');
        if (priceRangeFilter && this.originalPriceRange) {
          const minInput = priceRangeFilter.querySelector('#AlgoliaPriceInputFrom');
          const maxInput = priceRangeFilter.querySelector('#AlgoliaPriceInputTo');
          
          if (minInput && maxInput) {
            minInput.value = this.originalPriceRange.min;
            maxInput.value = this.originalPriceRange.max;
            
            // Update range sliders
            const rangeMin = priceRangeFilter.querySelector('input[type="range"]:first-child');
            const rangeMax = priceRangeFilter.querySelector('input[type="range"]:last-child');
            
            if (rangeMin && rangeMax) {
              rangeMin.value = this.originalPriceRange.min;
              rangeMax.value = this.originalPriceRange.max;
              
              // Update visual styles
              const priceRange = priceRangeFilter.querySelector('price-range');
              if (priceRange && priceRange.updateRangeStyles) {
                priceRange.updateRangeStyles();
              }
            }
          }
        }
        
        // Update filters and results
        this.appliedFilters = this.buildFilterString();
        this.page = 0;
        this.fetchFilteredResults();
        
        // Clear URL filters
        const searchParams = new URLSearchParams(window.location.search);
        searchParams.delete('filters');
        searchParams.delete('page'); // Reset page when filters change
        
        history.replaceState(`Filtered Collection - ${this.collectionHandle}`, '', `?${searchParams.toString()}`);
      }
    }
  )
}

// PriceRange custom element for Algolia
if (!customElements.get('price-range')) {
  class PriceRange extends HTMLElement {
    constructor() {
      super();

      this.rangeMin = this.querySelector('input[type="range"]:first-child');
      this.rangeMax = this.querySelector('input[type="range"]:last-child');
      this.inputMin = this.querySelector('#AlgoliaPriceInputFrom');
      this.inputMax = this.querySelector('#AlgoliaPriceInputTo');
      this.EPS = 0.01;

      if (this.rangeMin && this.rangeMax) {
        this.rangeMin.addEventListener('change', this.onRangeMinChange.bind(this));
        this.rangeMax.addEventListener('change', this.onRangeMaxChange.bind(this));
        this.rangeMin.addEventListener('input', this.onRangeMinInput.bind(this));
        this.rangeMax.addEventListener('input', this.onRangeMaxInput.bind(this));
      }
    }

    onInputMinChange(event) {
      event.preventDefault();
      const maxValue = parseCurrency(this.inputMax.value) || parseFloat(this.inputMax.max);
      const minValue = parseFloat(this.inputMin.min);
      let next = parseCurrency(event.target.value);
      if (isNaN(next)) next = minValue;
      next = Math.max(Math.min(next, maxValue - this.EPS), minValue);
      this.rangeMin.value = next;
      event.target.value = formatCurrency(next);
      this.updateRangeStyles();
    }

    onInputMaxChange(event) {
      event.preventDefault();
      const minValue = parseCurrency(this.inputMin.value) || parseFloat(this.inputMin.min);
      const maxValue = parseFloat(this.inputMax.max);
      let next = parseCurrency(event.target.value);
      if (isNaN(next)) next = maxValue;
      next = Math.min(Math.max(next, minValue + this.EPS), maxValue);
      this.rangeMax.value = next;
      event.target.value = formatCurrency(next);
      this.updateRangeStyles();
    }

    onRangeMinChange(event) {
      event.stopPropagation();
      this.inputMin.value = formatCurrency(event.target.value);
      this.inputMin.dispatchEvent(new Event('change', { bubbles: true }));
    }

    onRangeMaxChange(event) {
      event.stopPropagation();
      this.inputMax.value = formatCurrency(event.target.value);
      this.inputMax.dispatchEvent(new Event('change', { bubbles: true }));
    }

    onRangeMinInput(event) {
      const maxValue = parseCurrency(this.inputMax.value) || parseFloat(event.target.max);
      const coerced = Math.min(parseFloat(event.target.value), maxValue - this.EPS);
      event.target.value = coerced;
      this.inputMin.value = formatCurrency(coerced);
      this.updateRangeStyles();
    }

    onRangeMaxInput(event) {
      const minValue = parseCurrency(this.inputMin.value) || parseFloat(event.target.min);
      const coerced = Math.max(parseFloat(event.target.value), minValue + this.EPS);
      event.target.value = coerced;
      this.inputMax.value = formatCurrency(coerced);
      this.updateRangeStyles();
    }

    updateRangeStyles() {
      const rangeWrapper = this.querySelector('.range-wrapper');
      if (rangeWrapper && this.rangeMin && this.rangeMax) {
        const minProgress = (parseFloat(this.rangeMin.value) / parseFloat(this.rangeMin.max)) * 100;
        const maxProgress = (parseFloat(this.rangeMax.value) / parseFloat(this.rangeMax.max)) * 100;
        rangeWrapper.style.setProperty('--range-min', `${minProgress}%`);
        rangeWrapper.style.setProperty('--range-max', `${maxProgress}%`);
      }
    }
  }

  customElements.define('price-range', PriceRange);
}

class AlgoliaFacetSort extends HTMLElement {
  constructor() {
    super();

    Motion.inView(this, this.init.bind(this), { margin: '200px 0px 200px 0px' });

    this.addEventListener('change', this.onChange.bind(this));
    if (this.button) this.button.addEventListener('click', this.show.bind(this));
    if (this.close) this.close.addEventListener('click', this.hide.bind(this));
    document.addEventListener('click', this.onWindowClick.bind(this));
  }

  get listbox() {
    return this.querySelector('[data-sort-listbox]');
  }

  get selection() {
    return this.querySelector('[data-selection-label]');
  }

  get button() {
    return this.querySelector('[data-sort-by]');
  }

  get close() {
    return this.querySelector('[data-sort-close]');
  }

  init() {
    this.initButton();
    if (this.listbox) {
      this.style.setProperty('--facet-listbox-height', `${this.listbox.getBoundingClientRect().height}px`);
    }
    
    // Initialize with current sort value from URL
    const searchParams = new URLSearchParams(window.location.search);
    const currentIndex = searchParams.get('index');
    if (currentIndex) {
      const sortInput = this.querySelector(`input[value="${currentIndex}"]`);
      if (sortInput) {
        sortInput.checked = true;
        const sortLabel = sortInput.nextElementSibling;
        if (sortLabel) {
          const selectionLabel = this.querySelector('[data-selection-label]');
          this.selection.innerText = sortLabel.textContent;
          this.initButton();
        }
      }
    }
  }

  initButton() {
    const value = this.selection.textContent;
    const width = theme.getElementWidth(this.selection, value);
    this.style.setProperty('--facet-button-width', `${width}px`);
  }

  onChange(event) {
    this.selection.innerText = event.target.nextElementSibling.textContent;
    this.initButton();
    
    const sortValue = event.target.value;
    if (!sortValue) return;
    
    const algoliaGrid = this.closest('algolia-product-grid');
    if (!algoliaGrid) return;

    algoliaGrid.currentIndex = sortValue;
    algoliaGrid.page = 0;
    
    algoliaGrid.updateSortDisplays(sortValue);
    
    const searchParams = new URLSearchParams(window.location.search);
    searchParams.set('index', sortValue);
    searchParams.delete('page');
    history.replaceState(`Filtered Collection - ${algoliaGrid.collectionHandle}`, '', `?${searchParams.toString()}`);
    
    algoliaGrid.fetchFilteredResults();
    
    algoliaGrid.renderActiveFacets();
    
    this.hide();
  }

  show() {
    this.button.setAttribute('open', '');
  }

  hide(immediate = true) {
    if (this.button.hasAttribute('open')) {
      setTimeout(() => {
        this.button.removeAttribute('open');
      }, 100);

      if (theme.config.isTouch || document.body.getAttribute('data-button-hover') === 'none') return;
      
      const btnFill = this.button.querySelector('[data-fill]');
      if (btnFill) {
        Motion.animate(btnFill, { y: ['0%', immediate ? '0%' : '-76%'] }, { duration: 0.6, delay: immediate ? 0 : 0.2 });
      }
    }
  }

  onWindowClick(event) {
    if (!this.contains(event.target)) {
      if (this.button.hasAttribute('open')) {
        this.hide(false);
      }
    }
  }
}
customElements.define('algolia-facet-sort', AlgoliaFacetSort);

class AlgoliaFacetSticky extends HTMLElement {
  constructor() {
    super();

    new IntersectionObserver(this.handleIntersection.bind(this), { rootMargin: `-${screen.availHeight - 100}px 0px ${screen.availHeight}px 0px` }).observe(document.querySelector('.collection-section'));
  }

  get button() {
    return this.querySelector('.button');
  }

  handleIntersection(entries) {
    if (entries[0].isIntersecting) {
      Motion.animate(this.button, { opacity: 1, visibility: 'visible', transform: ['translateY(15px)', 'translateY(0)'] }, { duration: 1, easing: [0.16, 1, 0.3, 1] });
    }
    else {
      Motion.animate(this.button, { opacity: 0, visibility: 'hidden', transform: ['translateY(0)', 'translateY(15px)'] }, { duration: 1, easing: [0.16, 1, 0.3, 1] });
    }
  }
}
customElements.define('algolia-facet-sticky', AlgoliaFacetSticky);
